import React from 'react';

import {
  ActionIcon,
  Checkbox,
  Paper,
  Select,
  Space,
  Text,
  TextInput,
  Title,
  Tooltip,
  createStyles,
  useMantineTheme,
} from '@mantine/core';
import { IconFilterOff, IconSearch, IconX } from '@tabler/icons-react';

import { IssuedDocumentSearchFilters } from '../types';

const useStyles = createStyles(theme => ({
  header: {
    padding: theme.spacing.lg,
    background: theme.colors.gray[0],
    borderRadius: 0,
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    display: 'grid',
    gridTemplateColumns: 'auto max-content auto auto auto min-content',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `'title lastName search type status clear'`,
    alignItems: 'center',
    gap: theme.spacing.sm,
    [theme.fn.smallerThan('lg')]: {
      gridTemplateColumns: 'max-content auto auto auto min-content',
      gridTemplateRows: 'auto auto',
      gridTemplateAreas: `
        'title title title title title'
        'lastName search type status clear'
      `,
    },
    [theme.fn.smallerThan('md')]: {
      gridTemplateColumns: 'max-content auto auto min-content',
      gridTemplateRows: 'auto auto auto auto',
      gridTemplateAreas: `
        'title title title title'
        'search search search search'
        'type type status status'
        'lastName . . clear'
      `,
    },
    [theme.fn.smallerThan('sm')]: {
      gridTemplateColumns: 'max-content auto min-content',
      gridTemplateRows: 'auto auto auto auto auto',
      gridTemplateAreas: `
        'title title title'
        'search search search'
        'type type type'
        'status status status'
        'lastName . clear'
      `,
    },
  },
  title: {
    gridArea: 'title',
  },
  lastName: {
    gridArea: 'lastName',
  },
  search: {
    gridArea: 'search',
  },
  type: {
    gridArea: 'type',
  },
  status: {
    gridArea: 'status',
  },
  clear: {
    gridArea: 'clear',
  },
}));

const IssuedDocumentsPageHeader = ({
  searchFilters,
  initialFilterValues,
  setSearchFilters,
}: {
  searchFilters: IssuedDocumentSearchFilters;
  initialFilterValues: IssuedDocumentSearchFilters;
  setSearchFilters: React.Dispatch<React.SetStateAction<IssuedDocumentSearchFilters>>;
}): JSX.Element => {
  const theme = useMantineTheme();
  const { classes } = useStyles();

  const handleTypeChange = (value: IssuedDocumentSearchFilters['type']): void => {
    setSearchFilters(prev => ({ ...prev, type: value }));
  };
  const handleStatusChange = (value: string | null): void => {
    setSearchFilters(prev => ({ ...prev, isSigned: value }));
  };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.currentTarget?.value || '';
    setSearchFilters(prev => ({ ...prev, search: value }));
  };
  const handleIsArrangeByLastNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = !!e.currentTarget?.checked;
    setSearchFilters(prevValue => ({ ...prevValue, isArrangeByLastName: value }));
  };

  const handleClearFilters = (): void => {
    setSearchFilters(initialFilterValues);
  };
  const handleClearSearchFilter = (): void => {
    setSearchFilters(prevValue => ({ ...prevValue, search: '' }));
  };

  return (
    <Paper className={classes.header}>
      <Title order={1} size='h1' pr='xl' className={classes.title}>
        Issued Documents
      </Title>
      <Checkbox
        className={classes.lastName}
        checked={searchFilters.isArrangeByLastName}
        onChange={handleIsArrangeByLastNameChange}
        label={
          <Text color='gray' fs='italic'>
            Arrange by Last Name
          </Text>
        }
      />
      <TextInput
        data-cy='searchDescriptionDocuments'
        className={classes.search}
        placeholder='Search by description'
        icon={<IconSearch size={theme.fontSizes.md} />}
        onChange={handleSearchChange}
        value={searchFilters.search}
        rightSection={
          !!searchFilters.search ? (
            <ActionIcon onClick={handleClearSearchFilter} size='md'>
              <IconX size={theme.fontSizes.md} />
            </ActionIcon>
          ) : (
            <Space />
          )
        }
      />
      <Select
        className={classes.type}
        placeholder='Select type'
        data-cy='selectTypeDocuments'
        data={[
          { label: 'Contract', value: 'contract', 'data-cy': 'contractOption' },
          { label: 'Waiver', value: 'waiver', 'data-cy': 'waiverOption' },
        ]}
        value={searchFilters.type}
        onChange={handleTypeChange}
        clearable
      />
      <Select
        data-cy='selectStatusFilter'
        className={classes.status}
        placeholder='Select status'
        data={[
          { label: 'Signed', value: 'True', 'data-cy': 'signedOption' },
          { label: 'Not Signed', value: 'False', 'data-cy': 'notSignedOption' },
        ]}
        value={searchFilters.isSigned}
        onChange={handleStatusChange}
        clearable
      />
      <Tooltip label='Clear filters' className={classes.clear}>
        <ActionIcon onClick={handleClearFilters} data-cy='filterButton'>
          <IconFilterOff />
        </ActionIcon>
      </Tooltip>
    </Paper>
  );
};

export default IssuedDocumentsPageHeader;
